import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { forgotPassword, verifyResetCode } from '../services/authService';

const RESEND_COOLDOWN_SECONDS = 60;

const inputClass =
  'appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 ' +
  'text-gray-900 focus:outline-none focus:ring-[#5C3BD1] focus:border-[#5C3BD1] focus:z-10 sm:text-sm';
const primaryButtonClass =
  'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium ' +
  'rounded-md text-white bg-[#5C3BD1] hover:bg-[#2F1E6B] focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
  'focus:ring-[#5C3BD1] disabled:opacity-60 disabled:cursor-not-allowed max-md:min-h-[44px]';

/**
 * Two-step password reset (docs/password-reset-otp-plan.md):
 *   1. email  -> POST /auth/forgot-password  (6-digit code sent to WhatsApp + email)
 *   2. code   -> POST /auth/verify-reset-code (returns a short-lived resetToken)
 * then we hand over to the existing /reset-password/:token page.
 */
function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const requestCode = async () => {
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const result = await forgotPassword(email.trim());
      if (result.success) {
        setStep('code');
        setCode('');
        setResendIn(RESEND_COOLDOWN_SECONDS);
      } else if (result.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    await requestCode();
  };

  const handleResend = async () => {
    if (resendIn > 0 || busy) return;
    await requestCode();
    setInfo('A new code has been sent.');
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setBusy(true);
    try {
      const result = await verifyResetCode(email.trim(), code);
      if (result.success && result.resetToken) {
        navigate(`/reset-password/${result.resetToken}`);
      } else if (result.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError('That code is wrong or has expired. Check the digits or request a new one.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#5C3BD1] font-fredoka">
            Reset your password
          </h2>
          <p className="mt-3 text-center text-sm text-gray-600">
            {step === 'email'
              ? "We'll send a 6-digit code to the WhatsApp number and email on your account."
              : 'Enter the 6-digit code we just sent you.'}
          </p>
        </div>

        {step === 'email' && (
          <form className="mt-8 space-y-6" onSubmit={handleEmailSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={inputClass}
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">{error}</div>
            )}

            <button type="submit" disabled={busy} className={primaryButtonClass}>
              {busy ? 'Sending…' : 'Send my code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form className="mt-8 space-y-6" onSubmit={handleCodeSubmit}>
            <div className="text-sm text-gray-600 bg-[#F3F0FF] p-3 rounded-md">
              If an account exists for <span className="font-medium text-gray-900">{email.trim()}</span>, a
              code is on its way. Check WhatsApp first — it usually arrives within a few seconds. The email
              copy may take a minute and can land in junk.
            </div>

            <div>
              <label htmlFor="code" className="sr-only">
                6-digit code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                required
                autoFocus
                className={`${inputClass} text-center text-2xl tracking-[0.5em] font-semibold`}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            {info && (
              <div className="text-green-700 text-sm bg-green-50 p-3 rounded-md">{info}</div>
            )}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">{error}</div>
            )}

            <button type="submit" disabled={busy} className={primaryButtonClass}>
              {busy ? 'Checking…' : 'Verify code'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendIn > 0 || busy}
                className="font-medium text-[#5C3BD1] hover:text-[#2F1E6B] disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setInfo('');
                }}
                className="font-medium text-[#5C3BD1] hover:text-[#2F1E6B]"
              >
                Use a different email
              </button>
            </div>
          </form>
        )}

        <div className="text-sm text-center">
          <Link to="/login" className="font-medium text-[#5C3BD1] hover:text-[#2F1E6B]">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
